import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { theme } from '../theme';

/**
 * GlobalSearch — Cmd+K / Ctrl+K spotlight-style search.
 * Searches patients, treatments, appointments from a single input.
 *
 * Props:
 *   API_BASE
 *   onSelectPatient(patient)  — navigate to patient profile
 *   onNavigate(tab)           — switch to a tab
 *   onClose
 */
const GlobalSearch = ({ API_BASE, onSelectPatient, onNavigate, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ patients: [], quickActions: [] });
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const QUICK_ACTIONS = [
    { label: 'New Patient', icon: '👤', action: () => onNavigate('register') },
    { label: 'Calendar', icon: '📅', action: () => onNavigate('calendar') },
    { label: 'Dashboard', icon: '📊', action: () => onNavigate('dashboard') },
    { label: 'Reports', icon: '📋', action: () => onNavigate('analytics') },
    { label: 'Settings', icon: '⚙️', action: () => onNavigate('settings') },
    { label: 'Appointments', icon: '🕐', action: () => onNavigate('appointments') },
    { label: 'Recalls', icon: '🔔', action: () => onNavigate('recalls') },
    { label: 'Inventory', icon: '📦', action: () => onNavigate('inventory') },
    { label: 'Lab Orders', icon: '🔬', action: () => onNavigate('lab') },
    { label: 'Expenses', icon: '💰', action: () => onNavigate('expenses') },
    { label: 'Pharmacy', icon: '💊', action: () => onNavigate('pharmacy') },
    { label: 'Insurance', icon: '🛡️', action: () => onNavigate('insurance') },
    { label: 'Marketing', icon: '📢', action: () => onNavigate('marketing') },
    { label: 'AI Analyzer', icon: '🧠', action: () => onNavigate('analyzer') },
    { label: 'Packages', icon: '🎁', action: () => onNavigate('packages') },
    { label: 'Online Bookings', icon: '🌐', action: () => onNavigate('bookings') },
    { label: 'Admin', icon: '🔐', action: () => onNavigate('admin') },
  ];

  const search = useCallback(async (q) => {
    if (!q.trim()) {
      setResults({ patients: [], quickActions: QUICK_ACTIONS.slice(0, 6) });
      setSelectedIdx(0);
      return;
    }
    const lower = q.toLowerCase();

    // Filter quick actions
    const matchedActions = QUICK_ACTIONS.filter(a => a.label.toLowerCase().includes(lower));

    // Search patients
    try {
      const res = await axios.get(`${API_BASE}/patients`);
      const matchedPatients = res.data.filter(p =>
        (p.name || '').toLowerCase().includes(lower) ||
        (p.phone || '').includes(q) ||
        (p.uhid || '').toLowerCase().includes(lower)
      ).slice(0, 8);
      setResults({ patients: matchedPatients, quickActions: matchedActions.slice(0, 4) });
    } catch (e) {
      setResults({ patients: [], quickActions: matchedActions.slice(0, 4) });
    }
    setSelectedIdx(0);
  }, [API_BASE]); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const allItems = [
    ...results.patients.map(p => ({ type: 'patient', data: p })),
    ...results.quickActions.map(a => ({ type: 'action', data: a })),
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, allItems.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && allItems[selectedIdx]) {
      e.preventDefault();
      const item = allItems[selectedIdx];
      if (item.type === 'patient') { onSelectPatient(item.data); onClose(); }
      if (item.type === 'action') { item.data.action(); onClose(); }
    }
    if (e.key === 'Escape') onClose();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: '15vh',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '16px',
          width: '580px', maxWidth: '95vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}>

        {/* Search input */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.1rem', opacity: 0.4 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search patients, UHID, phone, or navigate..."
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: '1.05rem',
              color: theme.navy, fontWeight: '500',
            }}
          />
          <kbd style={{ padding: '2px 8px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '0.72rem', color: '#888', border: '1px solid #ddd' }}>ESC</kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {/* Patients */}
          {results.patients.length > 0 && (
            <div>
              <div style={{ padding: '8px 20px 4px', fontSize: '0.68rem', color: theme.textSubtle, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Patients
              </div>
              {results.patients.map((p, i) => {
                const idx = i;
                const isSelected = selectedIdx === idx;
                return (
                  <div key={p._id}
                    onClick={() => { onSelectPatient(p); onClose(); }}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    style={{
                      padding: '10px 20px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '12px',
                      backgroundColor: isSelected ? theme.primaryLight : 'transparent',
                      transition: 'background 0.1s',
                    }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      backgroundColor: theme.primary + '18',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.9rem', fontWeight: '700', color: theme.primaryDark,
                    }}>
                      {(p.name || 'P').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: theme.navy, fontSize: '0.9rem' }}>{p.name}</div>
                      <div style={{ fontSize: '0.75rem', color: theme.textMuted }}>
                        UHID: {p.uhid} {p.phone && `• ${p.phone}`}
                      </div>
                    </div>
                    {isSelected && <span style={{ fontSize: '0.72rem', color: theme.textSubtle }}>↵ Enter</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick Actions */}
          {results.quickActions.length > 0 && (
            <div>
              <div style={{ padding: '8px 20px 4px', fontSize: '0.68rem', color: theme.textSubtle, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {query ? 'Actions' : 'Quick Actions'}
              </div>
              {results.quickActions.map((a, i) => {
                const idx = results.patients.length + i;
                const isSelected = selectedIdx === idx;
                return (
                  <div key={a.label}
                    onClick={() => { a.action(); onClose(); }}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    style={{
                      padding: '10px 20px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '12px',
                      backgroundColor: isSelected ? theme.primaryLight : 'transparent',
                      transition: 'background 0.1s',
                    }}>
                    <span style={{ fontSize: '1.1rem' }}>{a.icon}</span>
                    <span style={{ fontWeight: '500', color: theme.navy, fontSize: '0.9rem' }}>{a.label}</span>
                    {isSelected && <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: theme.textSubtle }}>↵ Enter</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* No results */}
          {query && allItems.length === 0 && (
            <div style={{ padding: '30px 20px', textAlign: 'center', color: '#bbb' }}>
              No results found for "{query}"
            </div>
          )}

          {/* Empty state */}
          {!query && (
            <div style={{ padding: '12px 20px 16px', fontSize: '0.78rem', color: theme.textSubtle, textAlign: 'center' }}>
              Type to search patients by name, UHID, or phone — or navigate to any page
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
